import { AdminUser } from "../types";
import { authService } from "./auth";

class AdminAuthService {
  private adminUser: AdminUser | null = null;
  private initialized = false;

  /**
   * Initialize admin authentication
   * Checks if the current authenticated user has admin privileges
   */
  async initialize(): Promise<void> {
    try {
      if (!authService.getToken()) {
        this.adminUser = null;
        this.initialized = true;
        return;
      }

      // Check if user has admin privileges by examining the JWT token
      const token = authService.getToken();
      if (token) {
        const decodedToken = this.decodeJWTToken(token);
        if (decodedToken && decodedToken.walletAddress) {
          // For now, we'll assume admin status is checked server-side
          // The actual admin verification happens when making admin API calls
          this.adminUser = {
            walletAddress: decodedToken.walletAddress,
            isAdmin: false, // Will be verified server-side
          };
        }
      }

      this.initialized = true;
    } catch (error) {
      console.error("Failed to initialize admin auth:", error);
      this.adminUser = null;
      this.initialized = true;
    }
  }

  /**
   * Check if current user is authenticated and potentially has admin access
   */
  isAuthenticated(): boolean {
    return !!authService.getToken() && !!this.adminUser;
  }

  /**
   * Get current admin user
   */
  getCurrentUser(): AdminUser | null {
    return this.adminUser;
  }

  /**
   * Get current wallet address
   */
  getWalletAddress(): string | null {
    return this.adminUser?.walletAddress || null;
  }

  /**
   * Get admin authentication token
   */
  getAdminToken(): string | null {
    return authService.getToken();
  }

  /**
   * Check if admin auth is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Logout admin user
   */
  logout(): void {
    this.adminUser = null;
    authService.logout();
  }

  /**
   * Update admin status after successful admin API call
   */
  confirmAdminStatus(): void {
    if (this.adminUser) {
      this.adminUser.isAdmin = true;
    }
  }

  /**
   * Reset admin status if admin API call fails
   */
  revokeAdminStatus(): void {
    if (this.adminUser) {
      this.adminUser.isAdmin = false;
    }
  }

  /**
   * Decode JWT token to extract wallet address
   */
  private decodeJWTToken(token: string): { walletAddress: string } | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Failed to decode JWT token:", error);
      return null;
    }
  }
}

export const adminAuthService = new AdminAuthService();